/** Normalize OCR / expected strings like production scanners (strip space, uppercase). */
export function normalizeOcrText(text: string): string {
  return text.replace(/\s+/g, '').toUpperCase();
}

/** Production shelf fix: OCR often reads letter I as digit 1 in N-1-M patterns (full-string). */
export function transformShelf1toI(input: string): string {
  const match = input.match(/^(\d+)-1-(\d+)$/);
  if (match) {
    return `${match[1]}-I-${match[2]}`;
  }
  return input;
}

/** Apply the same I/1 fix to every shelf-like N-1-M substring (mixed SKU+shelf OCR). */
export function applyShelf1toIInText(input: string): string {
  return input.replace(/(\d{1,2})-1-(\d+)/g, '$1-I-$2');
}

export function cleanOcrText(ocrText: string): string {
  return applyShelf1toIInText(normalizeOcrText(ocrText));
}

const SHELF_PATTERN = /\d{1,2}-[A-Z]-\d+/;

export interface OcrAbMatchResult {
  skuMatched: boolean | null;
  shelfMatched: boolean | null;
}

/**
 * Score one OCR arm against optional expected SKU / shelf.
 * null match = that expected field was not provided (excluded from accuracy KPI).
 */
export function scoreOcrAbArm(
  ocrText: string | null,
  expectedSku: string,
  expectedShelf: string
): OcrAbMatchResult {
  const skuExpected = expectedSku.trim();
  const shelfExpected = expectedShelf.trim();

  if (!ocrText || !ocrText.trim()) {
    return {
      skuMatched: skuExpected ? false : null,
      shelfMatched: shelfExpected ? false : null,
    };
  }

  const cleaned = cleanOcrText(ocrText);

  let skuMatched: boolean | null = null;
  if (skuExpected) {
    const expectedNorm = normalizeOcrText(skuExpected);
    skuMatched = cleaned.includes(expectedNorm) || cleaned === expectedNorm;
  }

  let shelfMatched: boolean | null = null;
  if (shelfExpected) {
    const expectedShelfNorm = transformShelf1toI(normalizeOcrText(shelfExpected));
    const shelfFromOcr = cleaned.match(SHELF_PATTERN)?.[0] ?? null;
    shelfMatched =
      shelfFromOcr === expectedShelfNorm || cleaned.includes(expectedShelfNorm);
  }

  return { skuMatched, shelfMatched };
}

export interface LatencyStats {
  meanMs: number;
  p50Ms: number;
  count: number;
}

export function computeLatencyStats(latenciesMs: number[]): LatencyStats | null {
  if (latenciesMs.length === 0) {
    return null;
  }
  const sorted = [...latenciesMs].sort((a, b) => a - b);
  const sum = sorted.reduce((acc, n) => acc + n, 0);
  const mid = Math.floor(sorted.length / 2);
  const p50Ms =
    sorted.length % 2 === 0
      ? Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)
      : sorted[mid]!;
  return {
    meanMs: Math.round(sum / sorted.length),
    p50Ms,
    count: sorted.length,
  };
}

export interface AccuracyStats {
  matched: number;
  total: number;
  percent: number;
}

export function computeAccuracyStats(matches: Array<boolean | null>): AccuracyStats | null {
  const scored = matches.filter((m): m is boolean => m !== null);
  if (scored.length === 0) {
    return null;
  }
  const matched = scored.filter(Boolean).length;
  return {
    matched,
    total: scored.length,
    percent: Math.round((matched / scored.length) * 1000) / 10,
  };
}
